import { BaseTable, Modal, MultipleChart, MultipleChartData, TableHeader } from "components"
import { LiquidityYearlyKey } from "../types/liquidityYearTable"
import { useToggle } from "utils"
import { TbChartBar } from "react-icons/tb"

import {
  ChartOptions
} from "chart.js"

import { useState } from "react"
import { persentage_category } from "../types/liquidityTableType"

type Props = {
  list: LiquidityYearlyKey[]
  isFilterReady: boolean
}

export default function LiquidityYearlyTable({ list, isFilterReady }: Props) {
  if (!list.length) return null

  const years = list[0].data.map((item) => item.year)

  let lastCategory = ""

  return (
    <div className="overflow-x-auto mt-6">
      <div className="max-h-[600px] overflow-y-auto">
      <BaseTable className="table table-bordered w-full">

        {/* HEADER LEVEL 1 */}
        <TableHeader>
          <tr className="bg-black text-white text-xs">
            <th rowSpan={2} className="text-left">
              KEY DESCRIPTION
            </th>

            {years.map((year) => (
              <th key={year} colSpan={2} className="text-center min-w-[100px]">
                {year}
              </th>
            ))}
           <th rowSpan={2} className="text-center">ACTION</th>
          </tr>

          {/* HEADER LEVEL 2 */}
          <tr className="bg-black text-white text-xs">
            {years.map((year) => (
              <>
                <th key={`budget-${year}`} className="text-center">
                  BUDGET
                </th>

                <th key={`real-${year}`} className="text-center">
                  REAL
                </th>
              </>
            ))}
          </tr>
        </TableHeader>

        <tbody>

          {list.map((item) => {
            const showCategory = item.category !== lastCategory
            lastCategory = item.category ?? ""

            return (
              <>
                {/* CATEGORY ROW */}
                {showCategory && (
                  <tr className="bg-base-300 font-semibold">
                    <td colSpan={years.length * 2 + 2}>
                      {item.category}
                    </td>
                  </tr>
                )}

                {/* DATA ROW */}
                <tr key={item.code_budget}>
                  <td className="font-medium !align-middle ps-10 min-w-[200px]">
                    {item.key_description}
                  </td>

                  {item.data.map((yearData) => (
                    <>
                      <td
                        key={`budget-${item.code_budget}-${yearData.year}`}
                        className="text-right"
                      >
                        {yearData.budget}
                        {yearData.budget && persentage_category.includes(item.code_budget ?? "") ? " %" : ""}
                      </td>

                      <td
                        key={`real-${item.code_budget}-${yearData.year}`}
                        className="text-right"
                      >
                        {yearData.actual}
                        {yearData.actual && persentage_category.includes(item.code_budget ?? "") ? " %" : ""}
                      </td>
                    </>
                  ))}
                  <td className="text-center !align-middle">
                    <ChartSectionYearly
                      title={item.key_description}
                      list={item.data}
                      disabled={!isFilterReady}
                    />
                  </td>
                </tr>
              </>
            )
          })}

        </tbody>
      </BaseTable>
      </div>
    </div>
  )
}


function ChartSectionYearly(params: {
  list: {
    year: number
    actual: string | number | null
    budget: string | number | null
    formula?: string
  }[]
  title: string
  disabled?: boolean
}) {

  const { isActive, toggle } = useToggle(false)
  const [chartType, setChartType] = useState<"line" | "bar">("line")

  const labels = params.list.map(item => String(item.year))

  const actualValues = params.list.map(item =>
    item.actual !== null ? Number(item.actual) : 0
  )

  // const budgetValues = params.list.map(item =>
  //   item.budget !== null ? Number(item.budget) : 0
  // )

  const data: MultipleChartData = {
    labels,
    datasets: [
      {
        label: "Actual",
        data: actualValues,
        borderColor: chartType === "line" ? "#3B82F6" : 'rgb(59, 130, 246, 0.5)',
        borderWidth: 2
      },
      // {
      //   label: "Budget",
      //   data: budgetValues,
      //   borderColor: "red",
      //   borderWidth: 2,
      //   // borderDash: chartType === "line" ? [6,6]:""
      // }
    ]
  }


  const options: ChartOptions<"bar" | "line"> = {

    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: 25
    },
  
    scales: chartType === "bar" ? {
      x: {
        position: "center",
        ticks: { display: false },
        grid: { display: false },
        border: { display: false }
      },

      y: {
        grid: {
          color: (ctx) =>
            ctx.tick.value === 0
              ? "rgb(255,193,7)"
              : "rgba(0,0,0,0)"
        },
        border: { display: false }
      }
    }:{

      x: {
        position: { y: 0 }, 

        ticks: {
          display: true
        },

        grid: { display: false },
        border: { display: false }
      },

    
      y: {
        grid: {
          color: (ctx) =>
            ctx.tick.value === 0
              ? "rgb(255,193,7)"
              : "rgba(0,0,0,0)"
        },
        border: { display: false }
      }
    
    
    },
  
    plugins: {
  
      legend: { display: false },
  
      datalabels: chartType === "bar"? {
          clip: false,
          labels: {

            year: {
              color: "#666",

              formatter: (_, context) =>
                labels[context.dataIndex],

              anchor: (context) => {
                const value =
                  context.dataset.data[context.dataIndex] as number
                return value > 0 ? "start" : "end"
              },

              align: (context) => {
                const value =
                  context.dataset.data[context.dataIndex] as number
                return value > 0 ? "bottom" : "top"
              }

            }

          }
        }
      : {
          clip: false,
          labels: {

            value: {
              color: "#666",

              display: (ctx) => ctx.dataset.data[ctx.dataIndex] !== 0,
    
              font: {
                weight: "bold"
              },
          
              formatter: (value: number) => {
                return value.toFixed(2)
              },
          
              anchor: (context) => {
                const value =
                  context.dataset.data[context.dataIndex] as number
                return value > 0 ? "start" : "end"
              },
              

              align: (context) => {
                const value =
                  context.dataset.data[context.dataIndex] as number
                return value > 0 ? "top" : "bottom"
              }

            }

          }
      }
  
    }
  
  }

  return (
    <>
      <button
        onClick={toggle}
        disabled={params.disabled}
        className="btn btn-xs btn-warning text-white"
      >
        <TbChartBar size={16} />
      </button>

      {isActive && (
        <Modal
          isOpen
          title={`${params.title.toUpperCase()} CHART`}
          closeModal={toggle}
          isdarktitle
        >
          <Modal.Body>

            <div className="flex justify-end mb-2">
              <select
                className="select select-sm select-bordered"
                value={chartType}
                onChange={(e) =>
                  setChartType(e.target.value as "line" | "bar")
                }
              >
                <option value="line">Line</option>
                <option value="bar">Bar</option>
              </select>
            </div>

            <div>
              <MultipleChart
                className="min-h-[450px]"
                chartType={chartType as "bar"}
                data={data}
                options={options}
              />
            </div>

          </Modal.Body>
        </Modal>
      )}
    </>
  )
}